/**
 * Registration Orchestrator
 *
 * Coordinates account registration through two paths:
 * 1. Primary (fds-id): Single API call handles ENS + stamp + GSOC mining
 * 2. Fallback: Piecemeal local operations (original flow)
 *
 * Account is always saved to localStorage first as a recovery anchor.
 */

import { logger } from '../../utils/logger'
import { getAccount, saveAccount } from '../storage'
import { fdsIdEnabled, getFdsIdClient } from '../fds-id/client'
import { requestAccountStamp } from '../swarm/stamps'
import { mineInboxKey } from '../swarm/gsoc'
import { getBeeOverlay } from '../swarm/client'
import { registerFairdropSubdomain } from '../ens'
import { exportToSwarm } from './backup'
import { createFairdropKeystore } from './keystore'
import { createFairdropSIWEMessage } from '../wallet/siwe'
import type { Account, InboxParams } from '../../types'
import type { EIP1193Provider } from '../wallet/types'

export type RegistrationStep =
  | 'authenticating'
  | 'registering'
  | 'encryption'
  | 'inbox'
  | 'backup'
  | 'complete'

export interface RegistrationCallbacks {
  onStatus: (status: string) => void
  onStep?: (step: RegistrationStep) => void
}

export interface RegistrationResult {
  success: boolean
  account: Account
  path: 'fds-id' | 'fallback'
  warnings: string[]
}

/**
 * Register a new account using fds-id (primary) or fallback path
 */
export async function registerAccount(
  subdomain: string,
  publicKeyHex: string,
  privateKeyHex: string,
  password: string,
  callbacks: RegistrationCallbacks,
  options?: { walletAddress?: string; walletProvider?: EIP1193Provider }
): Promise<RegistrationResult> {
  const warnings: string[] = []

  // Derive wallet address from private key if not provided
  const walletAddress = options?.walletAddress || await deriveWalletAddress(privateKeyHex)

  // E2E test-mode short-circuit. `import.meta.env.DEV` lets Vite tree-shake this branch out of
  // prod bundles — without it, an attacker-controlled localStorage flip silently fakes registration.
  if (import.meta.env.DEV && typeof window !== 'undefined' && window.localStorage?.getItem('__TEST_MODE__') === 'true') {
    const { hashPassword } = await import('../storage')
    const account: Account = {
      subdomain,
      publicKey: publicKeyHex,
      privateKey: privateKeyHex,
      passwordHash: await hashPassword(password),
      walletAddress,
      ensRegistered: true,
      created: Date.now(),
    }
    saveAccount(account)
    callbacks.onStep?.('complete')
    callbacks.onStatus('Test account created')
    return { success: true, account, path: 'fallback', warnings }
  }

  // Try fds-id primary path first
  if (fdsIdEnabled()) {
    try {
      return await registerViaFdsId(
        subdomain, publicKeyHex, privateKeyHex, password, walletAddress, callbacks, warnings,
        options?.walletProvider
      )
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      logger.warn('[Registration] fds-id path failed, falling back:', err.message)
      warnings.push(`fds-id registration failed: ${err.message}`)
    }
  }

  // Fallback: piecemeal local operations
  return registerFallback(
    subdomain, publicKeyHex, privateKeyHex, password, walletAddress, callbacks, warnings
  )
}

/**
 * Primary path: register via fds-id server
 */
async function registerViaFdsId(
  subdomain: string,
  publicKeyHex: string,
  privateKeyHex: string,
  password: string,
  walletAddress: string,
  callbacks: RegistrationCallbacks,
  warnings: string[],
  walletProvider?: EIP1193Provider
): Promise<RegistrationResult> {
  const client = getFdsIdClient()

  // 1. Create SIWE auth (server requires it)
  callbacks.onStep?.('authenticating')
  callbacks.onStatus('Authenticating...')
  logger.debug('[Registration] Creating SIWE auth')

  let siweMessage: string
  let signature: string

  if (walletProvider) {
    // Use the browser wallet provider to sign SIWE.
    // This ensures the SIWE-authenticated address matches the walletAddress
    // that is sent in the request body, satisfying the server's wallet mismatch check.
    const { message } = await createFairdropSIWEMessage(walletAddress)
    siweMessage = message
    signature = await walletProvider.request({
      method: 'personal_sign',
      params: [siweMessage, walletAddress],
    }) as string
  } else {
    // Fallback: sign with the encryption key (used when no browser wallet is available)
    const { Wallet } = await import('ethers')
    const signerKey = privateKeyHex.startsWith('0x') ? privateKeyHex : `0x${privateKeyHex}`
    const signer = new Wallet(signerKey)
    const { message } = await createFairdropSIWEMessage(signer.address)
    siweMessage = message
    signature = await signer.signMessage(siweMessage)
  }

  // 2. Register via fds-id (ENS + stamp + GSOC in one call)
  callbacks.onStep?.('registering')
  callbacks.onStatus('Registering account...')
  logger.debug('[Registration] Calling fds-id register')

  const result = await client.register({
    subdomain,
    publicKey: publicKeyHex.startsWith('0x') ? publicKeyHex : `0x${publicKeyHex}`,
    walletAddress,
    message: siweMessage,
    signature,
  })

  // 2. Build account from response
  const account: Account = {
    subdomain,
    publicKey: publicKeyHex,
    privateKey: privateKeyHex,
    passwordHash: '', // Set below
    walletAddress,
    created: Date.now(),
    ensRegistered: result.ens.registered,
  }

  // Save stamp info
  if (result.stamp) {
    account.stampId = result.stamp.batchId
    account.stampCapacity = result.stamp.capacity
    account.stampTotalCapacity = result.stamp.capacity
  } else {
    warnings.push('Stamp creation failed on server')
  }

  // Save inbox params
  if (result.inbox) {
    account.inboxParams = {
      targetOverlay: result.inbox.targetOverlay,
      baseIdentifier: result.inbox.baseIdentifier,
      proximity: result.inbox.proximity,
      recipientPublicKey: publicKeyHex,
    }
  } else {
    warnings.push('GSOC inbox setup failed on server')
  }

  // Hash password and save account
  const { hashPassword } = await import('../storage')
  account.passwordHash = await hashPassword(password)
  saveAccount(account)

  // 3. Skip stamp polling -- stamp will activate in background.
  // We already have the batchId which is sufficient to proceed.
  // Stamp usability is only needed when actually sending files.
  if (result.stamp) {
    logger.debug('[Registration] Stamp batchId saved, skipping usability polling (will activate in background)')
  }

  // 4. Create keystore and save locally + upload to Swarm
  callbacks.onStep?.('backup')
  if (password) {
    callbacks.onStatus('Backing up account...')
    try {
      const latestAccount = getAccount(subdomain) || account
      const keystoreJson = await createFairdropKeystore(latestAccount, password)

      // Always save keystore to local disk first
      downloadKeystoreFile(keystoreJson, subdomain)
      logger.debug('[Registration] Keystore downloaded to disk')

      // Also upload to Swarm if stamp is available
      if (result.stamp) {
        const keystoreObj = JSON.parse(keystoreJson) as object
        const uploadResult = await client.uploadKeystore({
          keystore: keystoreObj,
          stampId: result.stamp.batchId,
          password,
        })
        logger.debug('[Registration] Keystore uploaded:', uploadResult.swarmRef.slice(0, 12))
      }
    } catch (uploadError) {
      const err = uploadError instanceof Error ? uploadError : new Error(String(uploadError))
      logger.warn('[Registration] Keystore backup failed:', err.message)
      warnings.push('Backup failed (can be retried from Settings)')
    }
  }

  // Re-read latest account state
  callbacks.onStep?.('complete')
  const finalAccount = getAccount(subdomain) || account

  return {
    success: true,
    account: finalAccount,
    path: 'fds-id',
    warnings,
  }
}

/**
 * Fallback path: piecemeal local operations (original flow)
 */
async function registerFallback(
  subdomain: string,
  publicKeyHex: string,
  privateKeyHex: string,
  password: string,
  walletAddress: string,
  callbacks: RegistrationCallbacks,
  warnings: string[]
): Promise<RegistrationResult> {
  const account: Account = {
    subdomain,
    publicKey: publicKeyHex,
    privateKey: privateKeyHex,
    passwordHash: '', // Set below
    walletAddress,
    created: Date.now(),
  }

  // Hash password and save account (recovery anchor)
  const { hashPassword } = await import('../storage')
  account.passwordHash = await hashPassword(password)
  saveAccount(account)

  // 1. Mine GSOC inbox locally
  callbacks.onStep?.('inbox')
  callbacks.onStatus('Setting up inbox...')
  try {
    const inboxParams = await setupGSOCInboxLocal(account)
    if (inboxParams) {
      const latestAccount = getAccount(subdomain) || account
      saveAccount({ ...latestAccount, inboxParams })
    }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))
    logger.warn('[Registration] GSOC inbox setup failed:', err.message)
    warnings.push('Inbox setup failed')
  }

  // 2. Request stamp
  callbacks.onStep?.('encryption')
  callbacks.onStatus('Requesting storage allocation...')
  try {
    const stamp = await requestAccountStamp(privateKeyHex, subdomain)
    if (stamp.shared) {
      // Shared/sponsored stamp — use for uploads but don't save as personal
      logger.info('[Registration] Using shared stamp for uploads (not saved as personal)')
      warnings.push('No personal stamp created — using shared stamp for now')
    } else {
      const latestAccount = getAccount(subdomain) || account
      saveAccount({
        ...latestAccount,
        stampId: stamp.batchId,
        stampExpiresAt: stamp.expiresAt,
        stampCapacity: stamp.remainingCapacity,
        stampTotalCapacity: stamp.totalCapacity,
      })
    }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))
    logger.warn('[Registration] Stamp request failed:', err.message)
    warnings.push('Storage allocation failed')
  }

  // 3. Register on ENS
  callbacks.onStep?.('registering')
  callbacks.onStatus('Registering on ENS...')
  try {
    const latestAccount = getAccount(subdomain) || account
    const ensResult = await registerFairdropSubdomain(
      subdomain,
      publicKeyHex,
      { gsocParams: latestAccount.inboxParams }
    )
    if (ensResult.success) {
      const acct = getAccount(subdomain) || account
      saveAccount({ ...acct, ensRegistered: true })
    } else {
      warnings.push('ENS registration failed')
    }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))
    logger.warn('[Registration] ENS registration failed:', err.message)
    warnings.push('ENS registration failed')
  }

  // 4. Save keystore locally + export backup to Swarm
  callbacks.onStep?.('backup')
  callbacks.onStatus('Backing up account...')
  try {
    const latestAccount = getAccount(subdomain) || account

    // Save keystore to local disk first
    const keystoreJson = await createFairdropKeystore(latestAccount, password)
    downloadKeystoreFile(keystoreJson, subdomain)
    logger.debug('[Registration] Keystore downloaded to disk')

    // Also upload to Swarm
    const backupResult = await exportToSwarm(latestAccount, password)
    if (!backupResult.success) {
      warnings.push('Swarm backup failed')
    }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))
    logger.warn('[Registration] Backup failed:', err.message)
    warnings.push('Backup failed')
  }

  callbacks.onStep?.('complete')
  const finalAccount = getAccount(subdomain) || account

  return {
    success: true,
    account: finalAccount,
    path: 'fallback',
    warnings,
  }
}

/**
 * Set up GSOC inbox locally (used by fallback path and imported account login)
 */
export async function setupGSOCInboxLocal(account: Account): Promise<InboxParams | null> {
  // Check if already set up
  const existingAccount = getAccount(account.subdomain)
  if (existingAccount?.inboxParams) {
    return existingAccount.inboxParams
  }

  const overlay = await getBeeOverlay()
  if (!overlay) {
    logger.warn('[Registration] No Bee overlay available, skipping GSOC inbox setup')
    return null
  }

  // Mine GSOC key with proximity 8
  const { params } = await mineInboxKey(overlay, 8)

  return {
    ...params,
    recipientPublicKey: account.publicKey,
  }
}

/**
 * Poll stamp status until usable (exponential backoff).
 * Exported for background polling after registration completes.
 */
export async function pollUntilStampUsable(batchId: string, maxWaitMs = 120_000): Promise<void> {
  const client = getFdsIdClient()
  let delay = 2000
  const start = Date.now()

  while (Date.now() - start < maxWaitMs) {
    const status = await client.pollStampStatus(batchId)
    if (status.usable) return
    if (!status.exists) throw new Error('Stamp does not exist')

    await sleep(delay)
    delay = Math.min(delay * 1.5, 15_000)
  }

  throw new Error('Stamp not ready within timeout')
}

/**
 * Derive wallet address from private key
 */
async function deriveWalletAddress(privateKeyHex: string): Promise<string> {
  const { Wallet } = await import('ethers')
  const key = privateKeyHex.startsWith('0x') ? privateKeyHex : `0x${privateKeyHex}`
  return new Wallet(key).address
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Trigger browser download of keystore JSON file
 */
function downloadKeystoreFile(keystoreJson: string, subdomain: string): void {
  const blob = new Blob([keystoreJson], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${subdomain}.fairdrop.keystore.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
